/**
 * Mention parsing utilities
 *
 * Storage format: @[Name](userId) inline in comment content.
 * Parsed on render into styled segments.
 */

export interface MentionToken {
  type: 'text' | 'mention';
  value: string;
  userId?: string;
}

const MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Parse a comment string into tokens of text and mentions.
 */
export function parseMentions(content: string): MentionToken[] {
  const tokens: MentionToken[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(MENTION_REGEX)) {
    const start = match.index!;
    if (start > lastIndex) {
      tokens.push({ type: 'text', value: content.slice(lastIndex, start) });
    }
    tokens.push({ type: 'mention', value: match[1]!, userId: match[2] });
    lastIndex = start + match[0].length;
  }

  if (lastIndex < content.length) {
    tokens.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return tokens;
}

/**
 * Extract mentioned user IDs from a comment string.
 */
export function extractMentionedUserIds(content: string): string[] {
  const ids: string[] = [];
  for (const match of content.matchAll(MENTION_REGEX)) {
    ids.push(match[2]!);
  }
  return ids;
}

/**
 * Convert mention markup to plain display text.
 */
export function mentionToPlainText(content: string): string {
  return content.replace(MENTION_REGEX, '@$1');
}
